import React from 'react'
import { FormattedMessage } from 'react-intl'
import { Header, Item, Panel } from '.'
import { PanelProps } from './panel'
import { HeaderProps } from './header'
import { ItemProps } from './item'
import {
  SlidingTutorialContent,
  CustomSlidingTutorialProps
} from './sliding-tutorial-content'
import appsGridAddIcon from '../../../images/tutorial/appsGridAddIcon.png'
import appsGridGraphIcon from '../../../images/tutorial/appsGridGraphIcon.png'
import appsGridQuickViewIcon from '../../../images/tutorial/appsGridQuickViewIcon.png'
import appsGridScoreIcon from '../../../images/tutorial/appsGridScoreIcon.png'
import { getApplicationsRoute, getApplicationDrilldownRoute } from '../route'
import { Button } from '@contrast-security-inc/design-system-buttons'

export function ApplicationsSlidingTutorial(props: CustomSlidingTutorialProps) {
  const sampleAppLink = props.appId
    ? getApplicationDrilldownRoute(props.orgUuid, props.appId)
    : getApplicationsRoute(props.orgUuid)

  const itemsConfig: ItemProps[] = [
    {
      title: {
        id: 'title_appsSlidingTutorial_appPage',
        defaultMessage: 'Your Applications Page'
      },
      description: {
        id: 'variable_appsSlidingTutorial_appPage',
        defaultMessage:
          'Any application being run on a server with the Contrast agent enabled will appear on this page. Navigate the app to start finding vulnerabilities.<div>Get back to the <a>Sample App</a> at any time</div>',
        values: {
          div: (...msg) => <div>{msg}</div>,
          a: (...msg) => <a href={sampleAppLink}>{msg}</a>
        }
      },
      imagePath: appsGridAddIcon,
      circleIndicators: [
        {
          selector: '[tutorial-highlight="appsPageIcon"]',
          radius: 52,
          yOffset: -24,
          xOffset: -21
        },
        {
          selector: '[tutorial-highlight="appsPage"]',
          radius: 60,
          yOffset: -28,
          xOffset: -2
        }
      ]
    },
    {
      title: {
        id: 'title_appsSlidingTutorial_manageApplications',
        defaultMessage: 'Manage Applications'
      },
      description: {
        id: 'detail_appsSlidingTutorial_manageApplications',
        defaultMessage:
          'Get the big picture of all applications in your portfolio. Quickly view existing vulnerabilities or active attacks to guide your attention.'
      },
      imagePath: appsGridScoreIcon,
      circleIndicators: [
        {
          selector: '[tutorial-highlight="appVulnerabilities"]',
          radius: 70,
          yOffset: -64,
          xOffset: -20
        },
        {
          selector: '[tutorial-highlight="archivedAppVulnerabilities"]',
          radius: 70,
          yOffset: -64,
          xOffset: -20
        }
      ]
    },
    {
      title: {
        id: 'title_appsSlidingTutorial_quickViews',
        defaultMessage: 'Use Quick Views'
      },
      description: {
        id: 'detail_appsSlidingTutorial_quickViews',
        defaultMessage:
          'As your portfolio grows, utilize filters to find applications faster or hone in on certain groupings.'
      },
      imagePath: appsGridQuickViewIcon,
      circleIndicators: [
        {
          selector: '[tutorial-highlight="quickView"]',
          radius: 44,
          yOffset: -24,
          xOffset: -12
        }
      ]
    },
    {
      title: {
        id: 'title_appsSlidingTutorial_getDetailedInfo',
        defaultMessage: 'Get Detailed Information'
      },
      description: {
        id: 'detail_appsSlidingTutorial_getDetailedInfo',
        defaultMessage:
          'Go ahead, dive in! Click into the row to see metrics, delve into findings, review libraries used, and manage settings.'
      },
      imagePath: appsGridGraphIcon,
      circleIndicators: [
        {
          selector: '[tutorial-highlight="appName"]',
          radius: 66,
          yOffset: -56,
          xOffset: -36
        }
      ]
    }
  ]

  const headerConfig: HeaderProps = {
    title: {
      id: 'title_appsSlidingTutorial_getAcquainted',
      defaultMessage: "Let's Get Acquainted"
    },
    subtitle: {
      id: 'detail_appsSlidingTutorial_showFeatures',
      defaultMessage: "If you don't mind, we'd love to show you around."
    }
  }

  const panelConfig: PanelProps = {
    renderHeader: () => {
      return <Header {...headerConfig} />
    },
    renderBody: () =>
      itemsConfig.map((item, index) => <Item key={index} {...item} />),
    renderFooter: (renderFooterProps) => {
      return (
        <Button {...renderFooterProps}>
          <FormattedMessage
            id="action_appsSlidingTutorial_diveIn"
            defaultMessage="Got it. Dive In!"
          />
        </Button>
      )
    }
  }

  return props.isVisible ? (
    <SlidingTutorialContent
      isOpen={props.isOpen}
      renderPanel={(renderPanelProps) => {
        return <Panel {...renderPanelProps} {...panelConfig} />
      }}
      tutorialKey={props.tutorialKey}
      orgUuid={props.orgUuid}
      yOffset={props.yOffset}
      zIndex={props.zIndex}
    />
  ) : null
}
